import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, PenSquare, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fetchBlogCategories, submitBlogArticle } from "../lib/api";

export default function BlogSubmitPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    author_name: "",
    author_email: "",
    title: "",
    excerpt: "",
    content: "",
    category: "temoignages",
    tags: "",
    website_url: "", // honeypot
  });

  useEffect(() => {
    fetchBlogCategories().then((d) => setCategories(d.categories || [])).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitBlogArticle({
        ...form,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
      });
      setSubmitted(true);
      toast.success("Article envoyé !", {
        description: "Olivier va le relire. Tu seras notifié une fois publié.",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi.";
      toast.error("Envoi impossible", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <SEO
          title="Article envoyé"
          description="Ta contribution a bien été reçue par la communauté Autopièce Malin."
          noindex
        />
        <section className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40">
            <CheckCircle2 className="h-9 w-9 text-emerald-300" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-slate-50">
            Merci pour ta contribution !
          </h1>
          <p className="mt-3 text-slate-400">
            Ton article est en file de modération. Olivier le relit sous 24-48 h.
            Si ça colle à la ligne éditoriale, il sera publié sur le blog et tu seras crédité en tant qu'auteur.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/blog")}
              data-testid="submit-success-back"
              className="border-slate-700 bg-slate-900/60"
            >
              Retour au blog
            </Button>
            <Button
              className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"
              onClick={() => {
                setSubmitted(false);
                setForm({
                  author_name: "",
                  author_email: "",
                  title: "",
                  excerpt: "",
                  content: "",
                  category: "temoignages",
                  tags: "",
                  website_url: "",
                });
              }}
            >
              Soumettre un autre article
            </Button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Partager un article · Blog Autopièce Malin"
        description="Partage ton astuce mécanique ou ton retour d'expérience avec la communauté. Tous les contenus sont relus avant publication."
      />
      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-8">
        <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
          Contribue au blog
        </span>
        <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
          Partage ton astuce ou ton retour d'atelier
        </h1>
        <p className="mt-3 text-sm text-slate-400">
          Un bon plan de référence OEM, un tuto de remontage, un conseil pour éviter une arnaque ?
          Rédige-le ici — Olivier relit chaque contribution avant publication.
        </p>

        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest text-emerald-300">
          <ShieldCheck className="h-3 w-3" />
          Modération active · pas de pub, pas de spam
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="blog-submit-form"
          className="mt-8 space-y-5 rounded-2xl border border-slate-800 bg-slate-900/40 p-6 sm:p-8"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="author_name">Nom ou pseudo *</Label>
              <Input
                id="author_name"
                data-testid="submit-field-name"
                required
                minLength={2}
                maxLength={80}
                value={form.author_name}
                onChange={(e) => update("author_name", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="author_email">E-mail *</Label>
              <Input
                id="author_email"
                data-testid="submit-field-email"
                type="email"
                required
                value={form.author_email}
                onChange={(e) => update("author_email", e.target.value)}
              />
              <p className="text-[10px] text-slate-500">
                Jamais publié. Sert à te notifier de la publication.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">Titre de l'article *</Label>
            <Input
              id="title"
              data-testid="submit-field-title"
              required
              minLength={10}
              maxLength={180}
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Ex. Comment j'ai remplacé mon kit distribution 1.6 HDi pour 180 € de pièces"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="category">Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger data-testid="submit-field-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="excerpt">Résumé court (optionnel)</Label>
            <Textarea
              id="excerpt"
              data-testid="submit-field-excerpt"
              rows={2}
              maxLength={300}
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              placeholder="Une accroche en 1-2 phrases qui donne envie de lire l'article."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="content">Contenu de l'article * (200 à 20 000 caractères)</Label>
            <Textarea
              id="content"
              data-testid="submit-field-content"
              required
              rows={12}
              minLength={200}
              maxLength={20000}
              value={form.content}
              onChange={(e) => update("content", e.target.value)}
              placeholder="Le markdown est accepté. **Gras**, # Titres, listes à puces…"
              className="font-mono text-sm"
            />
            <p className="text-[10px] text-slate-500">
              {form.content.length.toLocaleString("fr-FR")} caractères · markdown accepté
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tags">Mots-clés (séparés par des virgules)</Label>
            <Input
              id="tags"
              data-testid="submit-field-tags"
              value={form.tags}
              onChange={(e) => update("tags", e.target.value)}
              placeholder="distribution, 1.6 HDi, PSA, économie"
            />
          </div>

          {/* Honeypot (invisible aux humains) */}
          <div style={{ position: "absolute", left: "-10000px", top: "-10000px" }} aria-hidden="true">
            <Label htmlFor="website_url">Site web (laisser vide)</Label>
            <Input
              id="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={form.website_url}
              onChange={(e) => update("website_url", e.target.value)}
            />
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-4 text-xs text-slate-500">
            En soumettant cet article, tu certifies qu'il est de ta rédaction et autorises
            Autopièce Malin à le publier avec ton nom/pseudo en tant qu'auteur. Tu peux demander
            son retrait à tout moment par e-mail à olivier8300@gmail.com.
          </div>

          <Button
            type="submit"
            data-testid="submit-blog-button"
            disabled={loading}
            className="w-full gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
          >
            <PenSquare className="h-4 w-4" />
            {loading ? "Envoi en cours…" : "Envoyer pour relecture"}
          </Button>
        </form>
      </section>
    </>
  );
}
